import { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import {
    fetchTableColumnsRequest,
    selectTableColumns,
    selectTableColumnsLoading,
    selectTableColumnsError,
    selectTableColumnsLastFetched,
} from '../store/slices/database/tableColumnConfigSlice';
import { buildColumnRenderFunction } from '../components/common/DataContainer/columnRenderers';
import { TABLE_COLUMN_TEMPLATES } from '../config/tableColumnTemplates';

/**
 * Transforms API response records to column configs
 * Handles the JSONB config column structure from Supabase
 * @param {Array} records - Records from API response
 * @returns {Array} Transformed column configs
 */
const transformApiResponse = (records) => {
    if (!Array.isArray(records)) return [];

    return records.map(record => {
        // Parse config if it's a string (JSONB comes as string from some backends)
        let config = record.config;
        if (typeof config === 'string') {
            try {
                config = JSON.parse(config);
            } catch (e) {
                console.warn(`Failed to parse config for column "${record.column_key}":`, e);
                config = {};
            }
        }
        config = config || {};

        // Parse renderConfig if it's a string within config
        let renderConfig = config.renderConfig;
        if (typeof renderConfig === 'string') {
            try {
                renderConfig = JSON.parse(renderConfig);
            } catch (e) {
                console.warn(`Failed to parse renderConfig for column "${record.column_key}":`, e);
                renderConfig = {};
            }
        }

        return {
            key: record.column_key,
            label: config.label || record.column_key,
            displayOrder: parseInt(record.display_order, 10) || 0,
            isVisible: record.is_visible !== false,
            sortable: config.sortable === true,
            align: config.align || 'left',
            minWidth: config.minWidth ? parseInt(config.minWidth, 10) : undefined,
            width: config.width ? parseInt(config.width, 10) : undefined,
            renderType: config.renderType,
            renderConfig: renderConfig,
            cellStyle: config.cellStyle,
            headerStyle: config.headerStyle,
        };
    });
};

/**
 * Custom hook for fetching and transforming table column configurations
 *
 * @param {string} tableName - Identifier for the table (e.g., 'UserCompanies')
 * @param {Object} options - Configuration options
 * @param {Object} options.actions - Action handlers keyed by actionKey (e.g., { onSelect: fn, onEdit: fn })
 * @param {Object} options.context - Context data for conditional rendering (e.g., { selectedCompanyId: 123 })
 * @param {Array} options.fallbackColumns - Override default fallback columns
 * @param {boolean} options.skipFetch - Skip fetching from API (use only fallback/cache)
 *
 * @returns {Object} { columns, loading, error, refetch, isUsingFallback }
 */
export function useTableColumns(tableName, options = {}) {
    const {
        actions = {},
        context = {},
        fallbackColumns,
        skipFetch = false,
    } = options;

    const dispatch = useDispatch();
    const [t] = useTranslation();
    const theme = useTheme();

    // Select from Redux
    const configColumns = useSelector(selectTableColumns(tableName));
    const loading = useSelector(selectTableColumnsLoading(tableName));
    const error = useSelector(selectTableColumnsError(tableName));
    const lastFetched = useSelector(selectTableColumnsLastFetched(tableName));

    // Get domain ID from domainConfig
    const domainId = useSelector((state) => state.domainConfig?.data?.domain_id);

    // Determine if we should fetch (only fetch once if not already loaded)
    const shouldFetch = useMemo(() => {
        if (skipFetch) return false;
        if (!domainId) return false;
        if (loading) return false;
        if (lastFetched) return false; // Prevent infinite loop if API returns empty array
        return configColumns.length === 0;
    }, [skipFetch, domainId, loading, lastFetched, configColumns.length]);

    // Fetch columns on mount or when needed
    useEffect(() => {
        if (shouldFetch) {
            dispatch(fetchTableColumnsRequest({ tableName, domainId }));
        }
    }, [dispatch, tableName, domainId, shouldFetch]);

    // Manual refetch function
    const refetch = useCallback(() => {
        if (domainId) {
            dispatch(fetchTableColumnsRequest({ tableName, domainId }));
        }
    }, [dispatch, tableName, domainId]);

    // Determine source columns (API, fallback, or default)
    const sourceColumns = useMemo(() => {
        if (configColumns.length > 0) {
            return configColumns;
        }
        if (fallbackColumns) {
            return fallbackColumns;
        }
        return TABLE_COLUMN_TEMPLATES[tableName] || [];
    }, [configColumns, fallbackColumns, tableName]);

    // Transform configs into DataTable-compatible columns with render functions
    const columns = useMemo(() => {
        return sourceColumns
            .filter(col => col.isVisible !== false)
            .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
            .map(columnConfig => {
                const {
                    key,
                    label,
                    sortable,
                    align,
                    minWidth,
                    width,
                    headerStyle,
                    cellStyle,
                    renderConfig,
                } = columnConfig;

                // Support cellStyle from renderConfig (Option B) or from column level
                const finalCellStyle = cellStyle || renderConfig?.cellStyle;

                return {
                    key,
                    label,
                    sortable: sortable ?? false,
                    align: align ?? 'left',
                    minWidth,
                    width,
                    headerStyle,
                    cellStyle: finalCellStyle,
                    render: buildColumnRenderFunction(columnConfig, {
                        actions,
                        context,
                        t,
                        theme,
                    }),
                };
            });
    }, [sourceColumns, actions, context, t, theme]);

    return {
        columns,
        loading,
        error,
        refetch,
        isFromCache: configColumns.length > 0,
        isUsingFallback: configColumns.length === 0,
    };
}

// Export helper for transforming API response (used in saga)
export { transformApiResponse, TABLE_COLUMN_TEMPLATES };
